/**
 * Photos waiting to be sent from the worker page, kept on the phone (IndexedDB) so a lost signal or a closed
 * tab loses nothing. Separate from capture's store. Every call fails soft: no storage means no offline queue.
 */
export type QueuedShade = { id: string; token: string; blob: Blob; createdAt: number };

const DB_NAME = "hkp-shade-upload";
const STORE = "queue";

const openDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("No local storage"));
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const store = req.result.createObjectStore(STORE, { keyPath: "id" });
      store.createIndex("token", "token");
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("Could not open local storage"));
  });

const run = async <T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> => {
  const db = await openDb();
  try {
    return await new Promise<T | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const req = work(tx.objectStore(STORE));
      tx.oncomplete = () => resolve(req ? (req as IDBRequest<T>).result : undefined);
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
};

export const loadQueued = async (token: string): Promise<QueuedShade[]> => {
  try {
    const rows = (await run<QueuedShade[]>("readonly", (s) => s.index("token").getAll(token))) || [];
    return rows.filter((r) => r && r.blob).sort((a, b) => a.createdAt - b.createdAt);
  } catch {
    return [];
  }
};

export const saveQueued = async (item: QueuedShade): Promise<void> => {
  try {
    await run("readwrite", (s) => s.put(item));
  } catch {
    // No storage: the photo still uploads now, it just won't survive closing the tab.
  }
};

export const removeQueued = async (id: string): Promise<void> => {
  try {
    await run("readwrite", (s) => s.delete(id));
  } catch {
    // Nothing kept, nothing to remove.
  }
};
